import type { FilterSchema } from '@/shared/components/filter/validation'
import type { TFunction } from 'i18next'
import type { WidgetPeriods } from '../types'

import { useWidgetParams } from '@/pages/dashboard/lib/useWidgetParams'
import { keys, prop, sum } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import { sortIn, useBIAnalyticQuery } from '@/entities/bi'
import { useSelectedCurrency } from '@/entities/currency'
import { PricingEngineType } from '@/entities/pricing-campaign'

import { ampli } from '@/shared/lib/ampli'
import { PALETTE } from '@/shared/lib/theme-palette'
import { formatMoney, NumberFormats } from '@/shared/tools/locale'

import DonutView from '../../DonutView/DonutView'
import { MainDashboardFilter } from '../../Filter'
import { Widget, WidgetContent, WidgetHeader } from '../../Widget'
import WidgetEmptyState from '../../WidgetEmptyState'
import WidgetErrorState from '../../WidgetErrorState'
import WidgetLoadingState from '../../WidgetLoadingState'

const getColor = (name: string) => {
	switch (name) {
		case PricingEngineType.RB:
			return PALETTE.orange[75]
		case PricingEngineType.ML:
			return PALETTE.blue[75]
		case PricingEngineType.MD:
			return PALETTE.purple[75]
		case PricingEngineType.Promo:
			return PALETTE.green[75]
		default:
			return PALETTE.neutral[150]
	}
}
const buildDataSeries = (
	data: [string, number][],
	total: number,
	t: TFunction
) =>
	data
		?.sort(sortIn(keys(PricingEngineType), prop('0')))
		?.map(([name, value]) => {
			const subvalue = +((value / total) * 100).toFixed(1)

			return {
				name: name
					? t(name, { defaultValue: name })
					: t('campaigns_om_title_not_reprice'),
				color: getColor(name),
				y: subvalue,
				subvalue,
				value,
			}
		})
export default function RevenueUnderManagementByEngineWidget({
	commonPeriods,
	commonFilters,
}: {
	commonPeriods: WidgetPeriods
	commonFilters: FilterSchema[]
}) {
	const { t } = useTranslation()

	const { params, onChangeParams } = useWidgetParams()
	const filters = [...commonFilters, ...(params.filters ?? [])]
	const currency = useSelectedCurrency()
	const biQuery = useBIAnalyticQuery({
		currency,
		label: 'RevenueUnderManagementByEngine',
		...commonPeriods.main,
		metrics: ['revenue'],
		dimensions: ['pricing_campaign_engine'],
		filters,
		date_aggregation: null,
	})
	const data = biQuery?.data?.data
	const total = sum(data?.map(prop('1')))

	return (
		<Widget className='relative flex flex-auto flex-col'>
			<WidgetHeader
				title={t('widget.revenue_under_management_by_engine.title')}
				tooltip={t('widget.revenue_under_management_by_engine.tooltip')}
				subtitle={t('widget.revenue_under_management_by_engine.subtitle')}
			>
				<MainDashboardFilter
					irremovableCount={commonFilters.length}
					filters={filters ?? []}
					onChange={(filters) => {
						ampli.dashboardBiApplyFilterRevenueUnderManagementByEngine({
							'filter by': filters.map(prop('name')).join(','),
						})
						onChangeParams({ ...params, filters })
					}}
				/>
			</WidgetHeader>
			<WidgetContent>
				<WidgetLoadingState isTinted={biQuery.isLoading} />
				<WidgetErrorState isTinted={biQuery.isError} />
				{data?.length && !biQuery.isLoading ? (
					<DonutView
						data={
							buildDataSeries(
								data as [string, number][],
								total,
								t
							)?.reverse() || []
						}
						formatValue={(val) =>
							formatMoney(val, currency, NumberFormats.Compact)
						}
						total={total}
					/>
				) : (
					<WidgetEmptyState />
				)}
			</WidgetContent>
		</Widget>
	)
}
